// Spin to Win (Monk edition) prize wheel. See blocks/spin-wheel.liquid.
//
// Members only, one spin per account per ISO week (Mon–Sun):
// - Guests see sign-in and a blurred wheel. Codes are only in the page config for signed-in members.
// - After a spin, a pop-up shows the result (wins: prize + code + copy).
// - "Got it" in that pop-up is a real submit button of the customer form: it saves the spin as tags
//   "spin-to-win, spin-<week>-<slice>, spin-prize-<slice>" (locks the week on the account and triggers the
//   "Spin to Win – claim hook" Flow). The pop-up can't be dismissed any other way until it's saved.
// - Page comes back ([data-spin-posted]) or this week's tag is on the account ([data-spin-done]): locked scene
//   (blurred wheel, result, "Show my code", "No more spins left this week"). "Show my code" reopens the pop-up
//   without the form.
// - Spun but not saved yet (reload / closed tab): the pop-up opens again on the next visit, same result.
// Calm spin (merchant: the fast spin flashed too much): 4 turns over 6s, slices dimmed while turning.

const SPIN_MS = 6000;
const SPIN_TURNS = 4;
// TEMPORARY (merchant, 3 Nov 2026): saving to the account hangs until permissions are sorted, so every spin lands on
// "no luck" and nothing is saved; "Got it" just closes the pop-up and locks the week in this browser. Set to false to restore.
const ALWAYS_NO_LUCK = true;

class SpinWheel extends HTMLElement {
  connectedCallback() {
    try {
      this.segments = JSON.parse(this.querySelector('[data-spin-config]')?.textContent || '[]');
    } catch {
      this.segments = [];
    }
    if (!this.segments.length || !this.hasAttribute('data-spin-member')) return;

    this.rotor = this.querySelector('[data-spin-rotor]');
    this.button = this.querySelector('[data-spin-button]');
    this.quick = this.querySelector('[data-spin-quick]');
    this.intro = this.querySelector('[data-spin-intro]');
    this.result = this.querySelector('[data-spin-result]');
    this.popup = this.querySelector('[data-spin-popup]');
    this.form = this.querySelector('#spin-save-form');
    this.tags = this.querySelector('[data-spin-tags]');
    this.week = this.dataset.spinWeek || '';
    this.storageKey = `monk-spin-v6-${this.dataset.spinCustomer || ''}`;

    this.bindPopup();

    // Saved on the account this week: locked on any device.
    const doneIndex = Number(this.dataset.spinDone) - 1;
    if (Number.isInteger(doneIndex) && this.segments[doneIndex]) {
      this.reveal(doneIndex, false);
      this.lock(doneIndex);
      return;
    }

    const saved = this.load();
    const posted = Boolean(this.querySelector('[data-spin-posted]'));
    if (saved && (posted || saved.posted)) {
      if (!saved.posted) this.store({ ...saved, posted: true });
      this.reveal(saved.index, false);
      this.lock(saved.index);
      return;
    }
    if (saved) {
      // Spun earlier but never saved: same result, save it now.
      this.reveal(saved.index, false);
      if (this.closest('[data-spin-sheet]')) {
        // In the cart drawer sheet the wheel is usually hidden on load: wait for a tap instead of opening the pop-up.
        this.result.hidden = false;
        const show = this.querySelector('[data-spin-show-code]');
        if (show) {
          show.hidden = false;
          show.onclick = () => this.openPopup(saved.index, true);
        }
      } else {
        this.openPopup(saved.index, true);
      }
      return;
    }

    this.button?.addEventListener('click', () => this.spin(false));
    this.quick?.addEventListener('click', () => this.spin(true));
  }

  load() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      if (saved && saved.week === this.week && Number.isInteger(saved.index) && this.segments[saved.index]) return saved;
    } catch {
      // Storage unavailable: the account tag still limits spins.
    }
    return null;
  }

  store(value) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch {
      // Ignore.
    }
  }

  pick() {
    if (ALWAYS_NO_LUCK) {
      const misses = this.segments.map((segment, i) => (segment.type === "nothing" ? i : -1)).filter((i) => i >= 0);
      if (misses.length) return misses[Math.floor(Math.random() * misses.length)];
    }
    const total = this.segments.reduce((sum, segment) => sum + Math.max(0, Number(segment.weight) || 0), 0);
    if (total <= 0) return Math.floor(Math.random() * this.segments.length);
    let roll = Math.random() * total;
    for (let index = 0; index < this.segments.length; index += 1) {
      roll -= Math.max(0, Number(this.segments[index].weight) || 0);
      if (roll < 0) return index;
    }
    return this.segments.length - 1;
  }

  // Rotation (deg) that puts the middle of a slice under the pointer at the top, with a little jitter.
  angleFor(index, turns) {
    const slice = 360 / this.segments.length;
    const jitter = (Math.random() - 0.5) * slice * 0.6;
    return turns * 360 + (360 - (index * slice + slice / 2)) + jitter;
  }

  spin(quick) {
    if (this.spinning || this.load()) return;
    this.spinning = true;
    if (this.button) this.button.disabled = true;
    if (this.quick) this.quick.hidden = true;
    const index = this.pick();
    // Remember right away, so a reload mid-spin gives the same result instead of a new spin.
    this.store({ index, week: this.week, at: Date.now() });
    const reduceMotion = quick || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.dataset.state = reduceMotion ? 'revealing' : 'spinning';
    this.rotor.style.transition = reduceMotion ? 'none' : `transform ${SPIN_MS}ms cubic-bezier(0.3, 0.45, 0.2, 1)`;
    this.rotor.style.transform = `rotate(${this.angleFor(index, reduceMotion ? 0 : SPIN_TURNS)}deg)`;
    window.setTimeout(() => {
      this.reveal(index, true);
      this.openPopup(index, true);
    }, reduceMotion ? 0 : SPIN_MS + 150);
  }

  reveal(index, fresh) {
    const segment = this.segments[index];
    if (!fresh) {
      this.rotor.style.transition = 'none';
      this.rotor.style.transform = `rotate(${this.angleFor(index, 0)}deg)`;
    }
    this.dataset.state = segment.type === 'nothing' ? 'no-luck' : 'won';
    this.querySelectorAll('.spin-seg').forEach((slice, i) => slice.classList.toggle('is-won', i === index));
    if (this.intro) this.intro.hidden = true;
    this.querySelector('[data-spin-prize]').textContent = segment.label;
    this.querySelector('[data-spin-message]').textContent = segment.message;
    const kicker = this.querySelector('[data-spin-kicker]');
    if (segment.type === 'nothing' && kicker) kicker.textContent = kicker.dataset.noLuck || kicker.textContent;
  }

  bindPopup() {
    if (!this.popup) return;
    // While the spin still has to be saved, Esc and the backdrop don't close it: "Got it" (the save) is the way out.
    this.popup.addEventListener('cancel', (event) => {
      if (this.mustSave) event.preventDefault();
    });
    this.popup.addEventListener('click', (event) => {
      if (event.target === this.popup && !this.mustSave) this.popup.close();
    });
    this.popup.querySelector('[data-spin-popup-dismiss]')?.addEventListener('click', () => this.popup.close());
    // Without saving (test mode): closing the result pop-up locks the week in this browser.
    this.popup.addEventListener("close", () => {
      if (!Number.isInteger(this.lockOnClose)) return;
      const index = this.lockOnClose;
      this.lockOnClose = null;
      this.store({ index, week: this.week, at: Date.now(), posted: true });
      this.lock(index);
      // In the cart drawer: fold the spin sheet back down after a short beat, so the cart is visible again.
      const sheet = this.closest("[data-spin-sheet]");
      if (sheet && typeof sheet.setOpen === "function") window.setTimeout(() => sheet.setOpen(false), 350);
    });
    // Don't disable the submit button: Shopify's spam check may re-submit the form with it.
    this.form?.addEventListener('submit', () => {
      const save = this.form.querySelector('[data-spin-popup-close]');
      if (!save) return;
      save.setAttribute('aria-disabled', 'true');
      save.style.pointerEvents = 'none';
      save.textContent = save.dataset.savingLabel || save.textContent;
    });
    const copy = this.popup.querySelector('[data-spin-copy]');
    copy?.addEventListener('click', async () => {
      const code = this.popup.querySelector('[data-spin-popup-code]')?.textContent || '';
      try {
        await navigator.clipboard.writeText(code);
        const label = copy.textContent;
        copy.textContent = copy.dataset.copiedLabel || label;
        window.setTimeout(() => (copy.textContent = label), 2000);
      } catch {
        // Clipboard blocked: the code is still visible to copy by hand.
      }
    });
  }

  // save = true: "Got it" saves the spin (form). save = false: just a reminder of the code ("Show my code").
  openPopup(index, save) {
    if (!this.popup) return;
    const segment = this.segments[index];
    const won = segment.type !== 'nothing';
    const slice = index + 1;
    this.mustSave = save && Boolean(this.form) && !ALWAYS_NO_LUCK;
    this.lockOnClose = save && !this.mustSave ? index : null;
    if (this.tags) this.tags.value = `spin-to-win, spin-${this.week}-${slice}, spin-prize-${slice}`;
    const kicker = this.popup.querySelector('[data-spin-popup-kicker]');
    if (kicker) kicker.textContent = won ? kicker.dataset.won : kicker.dataset.noLuck;
    this.popup.querySelector('[data-spin-popup-prize]').textContent = won ? segment.label : '';
    this.popup.querySelector('[data-spin-popup-prize]').hidden = !won;
    const code = this.popup.querySelector('[data-spin-popup-code]');
    code.textContent = segment.code || '';
    code.hidden = !segment.code;
    const copy = this.popup.querySelector('[data-spin-copy]');
    if (copy) copy.hidden = !segment.code;
    this.popup.querySelector('[data-spin-popup-message]').textContent = segment.message;
    if (this.form) this.form.style.display = this.mustSave ? '' : 'none';
    const dismiss = this.popup.querySelector('[data-spin-popup-dismiss]');
    if (dismiss) { dismiss.hidden = this.mustSave; dismiss.style.display = this.mustSave ? 'none' : ''; }
    if (!this.popup.open) this.popup.showModal();
  }

  // Locked scene: week used up.
  lock(index) {
    this.setAttribute('data-spin-locked', '');
    this.result.hidden = false;
    const show = this.querySelector('[data-spin-show-code]');
    if (show && this.popup && this.segments[index].code) {
      show.hidden = false;
      show.onclick = () => this.openPopup(index, false);
    }
    const over = this.querySelector('[data-spin-over]');
    if (over) over.hidden = false;
  }
}

if (!customElements.get('s-spin-wheel')) customElements.define('s-spin-wheel', SpinWheel);
